import httpClient from './httpClient';
import { parseApiError } from '../utils/errorHandler';

/**
 * Lista motoristas da empresa de transporte (paginado).
 * GET /api/motoristas
 */
function normalizeDriverRow(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = raw.id != null ? Number(raw.id) : null;
    if (id == null || Number.isNaN(id)) return null;
    const name =
        [raw.name, raw.fullName, raw.personName, raw.displayName].find(
            (x) => x != null && String(x).trim() !== ''
        ) || `Colaborador #${id}`;
    return { id, name: String(name).trim() };
}

function extractPagedContent(json) {
    if (!json || typeof json !== 'object') return [];
    if (Array.isArray(json.content)) return json.content;
    const emb = json._embedded;
    if (emb && typeof emb === 'object') {
        const first = Object.values(emb).find((v) => Array.isArray(v));
        if (first) return first;
    }
    return [];
}

/**
 * @param {{ page?: number, size?: number, sort?: string }} params
 * @returns {Promise<{ content: Array<{ id: number, name: string }>, page: { number: number, totalPages: number, last: boolean, first: boolean } }>}
 */
async function listDrivers({ page = 0, size = 30, sort = 'name,asc' } = {}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(size));
    if (sort) params.set('sort', sort);
    const response = await httpClient.get(`/api/motoristas?${params.toString()}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    const content = extractPagedContent(json)
        .map(normalizeDriverRow)
        .filter(Boolean);
    const pageMeta = json.page || {};
    const totalPages = pageMeta.totalPages ?? 0;
    const pageNumber = Math.max(0, pageMeta.number ?? 0);
    const last = totalPages === 0 || pageNumber >= totalPages - 1;
    const first = totalPages === 0 || pageNumber === 0;
    return {
        content,
        page: {
            number: pageNumber,
            totalPages,
            last,
            first,
        },
    };
}

/**
 * Atribui ou remove o motorista da entrega.
 * PATCH /api/entregas/{deliveryId}/motorista?customerId=...&driverId=...
 * Para limpar o motorista, omita driverId (ou use null no parâmetro desta função).
 *
 * @param {string|number} deliveryId
 * @param {string|number} customerId - obrigatório
 * @param {string|number|null|undefined} driverId - omitido na URL quando null/undefined
 */
async function setDeliveryDriver(deliveryId, customerId, driverId) {
    if (customerId == null || String(customerId).trim() === '') {
        throw new Error('O identificador do cliente (customerId) é obrigatório para atribuir o motorista.');
    }
    const params = new URLSearchParams();
    params.set('customerId', String(customerId));
    if (driverId != null && String(driverId).trim() !== '' && !Number.isNaN(Number(driverId))) {
        params.set('driverId', String(driverId));
    }
    const path = `/api/entregas/${encodeURIComponent(deliveryId)}/motorista?${params.toString()}`;
    const response = await httpClient.patch(path, null);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

const driverService = {
    listDrivers,
    setDeliveryDriver,
};

export default driverService;
